import React, { useEffect, useCallback, useMemo, useRef } from 'react'
import { Building2, GitBranchPlus, Plus, Sparkles, FlaskConical } from 'lucide-react'
import type { NodeProps } from '@xyflow/react'
import { BaseNode } from './Base/BaseNode'
import { NodeInteractionZone } from './Base/NodeInteractionZone'
import { useIsCollapsed } from '../hooks/useZoomState'
import { CATEGORY_STYLES } from '../../../constants/mindmapConfig'
import { useAutoSaveField } from './hooks/useAutoSaveField'
import { useVentureNodeUpdates } from './hooks/useVentureNodeUpdates'
import type { VentureFlowNode } from '../../../types/mindmap-components'
import type { DockAction } from './Base/BaseNodeTypes'
import {
  FieldContainer,
  FieldLabel,
  VentureNameInput,
  VentureFieldTextArea,
  SectionDivider,
} from './styles/VentureNodeStyles'
import { useReadOnlyContext } from '../../../context/readOnly'
import { NodeQualityChip, useNodeQualityScore } from '../../../features/nodeDataQuality'
import { VentureDocumentUpload } from './VentureDocumentUpload'
import { useNodeActions } from '../../../context/nodeActions'
import { useVentureEvidence } from '../hooks/useVentureEvidence'

// UI text configuration
const UI_TEXT = {
  categoryLabel: 'Venture',
  categorySubheading: 'Describe your idea and who it helps',
  categoryDescription:
    'This grounds every suggestion in your reality — your customer, your idea, and what you can build.',
  nameLabel: 'Project Name',
  businessModelLabel: 'What are you building?',
  capabilitiesLabel: 'Available Strengths',
  goalsLabel: 'Who is it for?',
  productTypeLabel: 'Format',
}

/**
 * Venture node component - displays project context as the parent of unmet-need nodes.
 * - Editable fields stored in mindmap JSON
 * - Has source handle on right side (for edge connecting to unmet-need children)
 */
export const VentureNode = React.memo((props: NodeProps<VentureFlowNode>) => {
  const { id, data } = props
  const nodeData = data
  const categoryStyle = CATEGORY_STYLES['venture']
  const { isReadOnly } = useReadOnlyContext()
  const qualityResult = useNodeQualityScore(id)
  const { handleDirectChildCreation, handleSynthesizeNeedsFromResearch } = useNodeActions()
  const chatApiRef = useRef<{ autoGenerate: () => void } | null>(null)

  const mindmapId = nodeData.mindmapId ?? undefined
  const { hasEvidence, loading: evidenceLoading } = useVentureEvidence(id, mindmapId)

  const handleChatReady = useCallback((api: { autoGenerate: () => void }) => {
    chatApiRef.current = api
  }, [])

  // Quality chip in header
  const renderHeaderActions = useCallback(
    () =>
      qualityResult ? (
        <NodeQualityChip qualityResult={qualityResult} nodeCategory="venture" inline />
      ) : null,
    [qualityResult]
  )

  // Dock action: Define Unmet Needs (dropdown with AI suggest + manual add + synthesize from research)
  const dockActions: DockAction[] = useMemo(
    () => [
      {
        id: 'add-need-group',
        icon: GitBranchPlus,
        label: 'Define Unmet Needs',
        group: 'branch',
        onClick: () => {},
        dropdownChildren: [
          {
            id: 'add-own',
            icon: Plus,
            label: 'Add your own',
            subtitle: 'Type it yourself',
            group: 'branch' as const,
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              handleDirectChildCreation(id, 'need')
            },
          },
          {
            id: 'ai-suggest',
            icon: Sparkles,
            label: 'AI suggest',
            subtitle: 'Generate options',
            group: 'branch' as const,
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              chatApiRef.current?.autoGenerate()
            },
          },
          {
            id: 'synthesize-from-research',
            icon: FlaskConical,
            label: 'Synthesize from research',
            subtitle: evidenceLoading
              ? 'Checking for research...'
              : hasEvidence
                ? 'Ground needs in your data'
                : 'Run a survey, interview, or upload research first.',
            group: 'branch' as const,
            disabled: !hasEvidence || evidenceLoading,
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              if (hasEvidence && !evidenceLoading) {
                void handleSynthesizeNeedsFromResearch(id)
              }
            },
          },
        ],
      },
    ],
    [id, handleDirectChildCreation, handleSynthesizeNeedsFromResearch, hasEvidence, evidenceLoading]
  )

  // Get update handlers
  const {
    updateVentureName,
    updateBusinessModel,
    updateCapabilities,
    updateConstraints,
    updateGoals,
    updateProductType,
  } = useVentureNodeUpdates()

  // Auto-save fields - name uses input (no auto-resize)
  const nameField = useAutoSaveField(
    nodeData.companyName,
    (value) => updateVentureName(id, value),
    {
      readOnly: isReadOnly,
    }
  )

  // Textarea fields with auto-resize
  const businessModelField = useAutoSaveField(
    nodeData.businessModelAndValueProp,
    (value) => updateBusinessModel(id, value),
    { autoResize: true, minHeight: 40, readOnly: isReadOnly }
  )

  const capabilitiesField = useAutoSaveField(
    nodeData.capabilitiesAndAssets,
    (value) => updateCapabilities(id, value),
    { autoResize: true, minHeight: 40, readOnly: isReadOnly }
  )

  const goalsField = useAutoSaveField(nodeData.goals, (value) => updateGoals(id, value), {
    autoResize: true,
    minHeight: 40,
    readOnly: isReadOnly,
  })

  const constraintsField = useAutoSaveField(
    nodeData.hardConstraints,
    (value) => updateConstraints(id, value),
    { autoResize: true, minHeight: 40, readOnly: isReadOnly }
  )

  const productTypeField = useAutoSaveField(
    nodeData.productType,
    (value) => updateProductType(id, value),
    { autoResize: true, minHeight: 40, readOnly: isReadOnly }
  )

  // Get collapsed state - uses optimized hook that only re-renders when crossing threshold
  const isCollapsed = useIsCollapsed()

  // Helper to resize all textareas
  const resizeAllTextareas = useCallback(() => {
    businessModelField.triggerResize()
    productTypeField.triggerResize()
    capabilitiesField.triggerResize()
    goalsField.triggerResize()
    constraintsField.triggerResize()
  }, [businessModelField, productTypeField, capabilitiesField, goalsField, constraintsField])

  // Trigger resize when node expands from collapsed state
  useEffect(() => {
    if (!isCollapsed) {
      // Double RAF to ensure DOM is fully painted before measuring
      const rafId = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          resizeAllTextareas()
        })
      })
      return () => cancelAnimationFrame(rafId)
    }
    return undefined
  }, [isCollapsed, resizeAllTextareas])

  return (
    <BaseNode
      id={id}
      nodeData={nodeData}
      icon={Building2}
      categoryLabel={UI_TEXT.categoryLabel}
      categorySubheading={UI_TEXT.categorySubheading}
      categoryDescription={UI_TEXT.categoryDescription}
      hideDefaultContent={true}
      onChatReady={handleChatReady}
      dockActions={dockActions}
      renderHeaderActions={renderHeaderActions}
      renderNodeContent={() => (
        <NodeInteractionZone>
          {/* Project Name - Editable */}
          <FieldContainer>
            <FieldLabel $color={categoryStyle.iconColor}>{UI_TEXT.nameLabel}</FieldLabel>
            <VentureNameInput
              ref={nameField.inputRef as React.RefObject<HTMLInputElement>}
              defaultValue={nodeData.companyName}
              onChange={nameField.handleChange}
              onFocus={nameField.handleFocus}
              onBlur={nameField.handleBlur}
              placeholder={isReadOnly ? '' : 'The name of your app, brand, or venture.'}
              readOnly={isReadOnly}
            />
          </FieldContainer>

          {/* What are you building? - Editable */}
          <SectionDivider $borderColor={categoryStyle.borderColor} />
          <FieldContainer>
            <FieldLabel $color={categoryStyle.iconColor}>{UI_TEXT.businessModelLabel}</FieldLabel>
            <VentureFieldTextArea
              ref={businessModelField.inputRef as React.RefObject<HTMLTextAreaElement>}
              defaultValue={nodeData.businessModelAndValueProp ?? ''}
              onChange={businessModelField.handleChange}
              onFocus={businessModelField.handleFocus}
              onBlur={businessModelField.handleBlur}
              placeholder={
                isReadOnly
                  ? ''
                  : 'e.g. An app that helps EV owners prove their battery health when selling their car'
              }
              readOnly={isReadOnly}
            />
          </FieldContainer>

          {/* Who is it for? - Editable */}
          <SectionDivider $borderColor={categoryStyle.borderColor} />
          <FieldContainer>
            <FieldLabel $color={categoryStyle.iconColor}>{UI_TEXT.goalsLabel}</FieldLabel>
            <VentureFieldTextArea
              ref={goalsField.inputRef as React.RefObject<HTMLTextAreaElement>}
              defaultValue={nodeData.goals ?? ''}
              onChange={goalsField.handleChange}
              onFocus={goalsField.handleFocus}
              onBlur={goalsField.handleBlur}
              placeholder={
                isReadOnly
                  ? ''
                  : 'e.g. EV owners who maintain their battery carefully and want full resale value'
              }
              readOnly={isReadOnly}
            />
          </FieldContainer>

          {/* Available Strengths - Editable */}
          <SectionDivider $borderColor={categoryStyle.borderColor} />
          <FieldContainer>
            <FieldLabel $color={categoryStyle.iconColor}>{UI_TEXT.capabilitiesLabel}</FieldLabel>
            <VentureFieldTextArea
              ref={capabilitiesField.inputRef as React.RefObject<HTMLTextAreaElement>}
              defaultValue={nodeData.capabilitiesAndAssets ?? ''}
              onChange={capabilitiesField.handleChange}
              onFocus={capabilitiesField.handleFocus}
              onBlur={capabilitiesField.handleBlur}
              placeholder={
                isReadOnly
                  ? ''
                  : "e.g. I'm a developer, I have design experience, I have access to X audience"
              }
              readOnly={isReadOnly}
            />
          </FieldContainer>

          {/* Format - Editable */}
          <SectionDivider $borderColor={categoryStyle.borderColor} />
          <FieldContainer>
            <FieldLabel $color={categoryStyle.iconColor}>{UI_TEXT.productTypeLabel}</FieldLabel>
            <VentureFieldTextArea
              ref={productTypeField.inputRef as React.RefObject<HTMLTextAreaElement>}
              defaultValue={nodeData.productType ?? ''}
              onChange={productTypeField.handleChange}
              onFocus={productTypeField.handleFocus}
              onBlur={productTypeField.handleBlur}
              placeholder={
                isReadOnly
                  ? ''
                  : 'e.g. mobile app, web platform, online course, physical product, marketplace...'
              }
              readOnly={isReadOnly}
            />
          </FieldContainer>

          {/* Project Boundaries - Legacy read-only display (hidden if empty) */}
          {nodeData.hardConstraints && (
            <>
              <SectionDivider $borderColor={categoryStyle.borderColor} />
              <FieldContainer $padding="8px 12px 12px 12px">
                <FieldLabel $color={categoryStyle.iconColor}>Project Boundaries</FieldLabel>
                <VentureFieldTextArea
                  ref={constraintsField.inputRef as React.RefObject<HTMLTextAreaElement>}
                  defaultValue={nodeData.hardConstraints}
                  readOnly={true}
                />
              </FieldContainer>
            </>
          )}

          {/* Supporting Files */}
          {!isReadOnly && (
            <>
              <SectionDivider $borderColor={categoryStyle.borderColor} />
              <VentureDocumentUpload
                ventureNodeId={id}
                mindmapId={nodeData.mindmapId ?? ''}
                isReadOnly={isReadOnly}
              />
            </>
          )}
        </NodeInteractionZone>
      )}
    />
  )
})
